import React from "react";
import PropTypes from "prop-types";
import logoLocal from '../../assets/images/logo.png';
import { displayNumbers, frenchDate, numberToWordsFrench } from "../../variables/constants";
import { libelleStatutLot } from './libellesStatut';

const formatMontantInt = (v) => displayNumbers(Math.trunc(Number(v) || 0));

const cellHeader = { padding: 6, border: '1px solid #d1d5db', textAlign: 'left', fontWeight: 700 };
const cellHeaderRight = { ...cellHeader, textAlign: 'right' };
const cell = { padding: 6, border: '1px solid #d1d5db' };
const cellRight = { ...cell, textAlign: 'right' };

const OrdreDeDepenseLotGlobal = React.forwardRef((props, ref) => {
    const { lot, expert, organisme, paiements } = props;

    /* LE BUREAU, ET NON L'ASSUREUR DU DOSSIER. Ce document porte le titre « Bureau
       National de la Carte Brune » en dur - mais son adresse, sa ville d'emission et
       son signataire venaient de `organisme`, qui est l'organisme MEMBRE du premier
       sinistre du lot : une compagnie d'assurance. L'en-tete annoncait donc le Bureau
       au-dessus de l'adresse d'une compagnie, et la piece etait signee du responsable
       de cette compagnie.

       `organisme` reste le repli, pour que le document ne perde rien si le bureau n'a
       pas pu etre charge. */
    const bureau = props?.bureau || organisme;

    const villeImpression = bureau?.ville || "Lomé";
    const signataireNom = bureau?.responsable || "";
    const signataireTitre = bureau?.titreResponsable || "Directeur Général";

    const totalHt = paiements?.reduce((acc, p) => acc + (Number(p.montant) || 0), 0) ?? 0;
    const totalRetenue = paiements?.reduce((acc, p) => acc + (Number(p.montantTaxe) || 0), 0) ?? 0;
    const netVerse = totalHt - totalRetenue;

    return (
        <div ref={ref} style={{
            width: "210mm",
            minHeight: "297mm",
            margin: "0 auto",
            backgroundColor: "white",
            padding: "20mm",
            fontFamily: "Arial, sans-serif",
            position: "relative",
            boxSizing: "border-box",
        }}>
            {/* En-tête : logo à gauche + titre BNCB centré */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 24 }}>
                <img src={logoLocal} alt="Logo" style={{ width: 110, height: 85, objectFit: 'contain', flexShrink: 0 }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1, margin: 0 }}>
                        BUREAU NATIONAL DE LA CARTE BRUNE
                    </h2>
                    {bureau?.adresse && (
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{bureau.adresse}</div>
                    )}
                </div>
                {/* Spacer droit pour équilibrer le flex (largeur égale au logo) */}
                <div style={{ width: 110, flexShrink: 0 }} aria-hidden="true" />
            </div>

            {/* Sous-titre */}
            <div style={{ textAlign: 'center', margin: '24px 0', fontSize: 16, fontWeight: 600, color: '#374151' }}>
                Ordre de dépense général - Lot {lot?.numeroLot || ''}
            </div>

            {/* Bénéficiaire global */}
            <div style={{ marginBottom: 24, fontSize: 14 }}>
                <div><strong>Bénéficiaire :</strong> {expert?.nomComplet || expert?.expertNomComplet || lot?.expertNomComplet || '-'}</div>
                <div><strong>Date du lot :</strong> {lot?.createdAt ? frenchDate(lot.createdAt) : '-'}</div>
                {/* EN FRANCAIS, PAS EN CONSTANTE. Le document imprimait
                    « REGLEMENT_TECHNIQUE_VALIDE » sur le papier. 'fr' : l'ordre de
                    depense reste en francais, quelle que soit la langue de l'ecran. */}
                <div><strong>Statut :</strong> {lot?.statut ? libelleStatutLot(lot.statut, 'fr') : '-'}</div>
            </div>

            {/* Tableau des expertises */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 12 }}>
                <thead>
                    <tr style={{ background: '#f3f4f6' }}>
                        <th style={cellHeader}>N° Sinistre</th>
                        <th style={cellHeader}>N° Règlement</th>
                        <th style={cellHeaderRight}>Montant HT</th>
                        <th style={cellHeaderRight}>Retenue</th>
                        <th style={cellHeaderRight}>Net versé</th>
                    </tr>
                </thead>
                <tbody>
                    {(paiements || []).map((p, idx) => {
                        const ht = Number(p.montant) || 0;
                        const retenue = Number(p.montantTaxe) || 0;
                        const net = ht - retenue;
                        return (
                            <tr key={p.paiementTrackingId || idx}>
                                <td style={cell}>{p.sinistreReference || '-'}</td>
                                <td style={cell}>{p.numeroPaiement || '-'}</td>
                                <td style={cellRight}>{formatMontantInt(ht)} F</td>
                                <td style={cellRight}>{formatMontantInt(retenue)} F</td>
                                <td style={cellRight}>{formatMontantInt(net)} F</td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background: '#f9fafb', fontWeight: 700 }}>
                        <td style={cell} colSpan={2}>TOTAUX</td>
                        <td style={cellRight}>{formatMontantInt(totalHt)} F</td>
                        <td style={cellRight}>{formatMontantInt(totalRetenue)} F</td>
                        <td style={cellRight}>{formatMontantInt(netVerse)} F</td>
                    </tr>
                </tfoot>
            </table>

            {/* Montant en lettres */}
            <div style={{ marginBottom: 32, fontSize: 13 }}>
                <strong>Net à verser :</strong> {numberToWordsFrench(netVerse)}.
            </div>

            {/* Signature */}
            <div style={{ marginTop: 64, textAlign: 'right', fontSize: 13 }}>
                <div>{villeImpression}, le {frenchDate(new Date().toISOString())}</div>
                <div style={{ marginTop: 48 }}>{signataireTitre}</div>
                <div style={{ fontWeight: 700 }}>{signataireNom}</div>
            </div>
        </div>
    );
});

OrdreDeDepenseLotGlobal.propTypes = {
    lot: PropTypes.object.isRequired,
    expert: PropTypes.object,
    organisme: PropTypes.object,
    /** Le bureau Carte Brune qui delivre la piece. A defaut, `organisme`. */
    bureau: PropTypes.object,
    paiements: PropTypes.array.isRequired,
};

OrdreDeDepenseLotGlobal.displayName = 'OrdreDeDepenseLotGlobal';

export default OrdreDeDepenseLotGlobal;
